<template>
  <el-col
    :span="24"
    id="check_processing"
    v-loading.body="showLoader"
    element-loading-text="Fetching page...">
    <el-row type="flex" justify="start" align="middle" id="check_processing-header">
      <HeaderInfoText :colspan="9" title="CHECK PROCESSING" :meta="meta" :selection="selection" />
      <el-col :span="6">
        <el-row type="flex" justify="center" v-if="meta.count > 20">
          <el-pagination
            layout="prev, pager, next, jumper"
            :total="meta.count"
            :page-size="20"
            @current-change="changePage"
            :current-page="currentPage"
          />
        </el-row>
      </el-col>
      <el-col :span="9" class="h-padded right-aligned-col">
        <el-row type="flex" justify="end" align="middle" style="padding: 0 12px;">
          <el-button
            type="danger"
            @click="confirmSignatureDialog = true"
            :disabled="!printableApsSelected || checkPrinterVisible"
            :loading="fetchingCheckData">
            {{ fetchingCheckData ? 'Fetching data' : 'Request Signature' }}
          </el-button>
          <el-button
            type="primary"
            @click="printChecks"
            :disabled="!printableApsSelected || checkPrinterVisible"
            :loading="fetchingCheckData">
            {{ fetchingCheckData ? 'Fetching data' : 'Print Selected Checks' }}
          </el-button>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="start" align="middle" style="padding: 0 12px;">
      <Filters
        :filters="filters"
        :filtersVisible="true"
        :filterSize="5"
        :applyDisabled="filteringShouldBeDisabled"
        :autoApply="false"
        clearAction="clearApState"
        clearMetadataAction="resetApMeta"
        searchAction="fetchPrintableApsWithQuery"
        ref="printablesFilter"
        @applied="handleSelectionChange([])"
        @filterChanged="handleFilterChange"
        @filterCleared="() => changePage(1)"
        @doneFiltering="getAppliedFilters">
        <template v-for="(f, i) in filters" v-slot:[`${f.type}${i}`]="props" :key="i">
          <component :ref="`${f.props.filterName}_filter`" :key="i" :is="f.type" :properties="f.props" />
        </template>
      </Filters>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <TransactionsConfPaymentList
          v-if="transactions"
          name="check_processing_conf_list"
          ref="check_conf_list"
          :aggridColumns="aggridColumns"
          :rows="transactions"
          :emitSelect="true"
          :baseQuery="baseQuery"
          :currentPage="currentPage"
          @filtersApplied="filtersApplied"
          @rowsLoaded="loadDraftChecksGrid"
          @onSelect="handleSelectionChange"
        />
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle">
      <el-col :span="24">
        <el-row>
          <el-col :span="8">
            <el-row>
              <h2 class="mt-3 mb-2 ms-2">Checks For Signature Approval</h2>
            </el-row>
          </el-col>
          <el-col :span="8">
            <el-row type="flex" justify="center" v-if="draftChecksMeta.count > 20">
              <el-pagination
                layout="prev, pager, next, jumper"
                :total="draftChecksMeta.count"
                :page-size="20"
                @current-change="changeDraftCheckPage"
                :current-page="currentDraftCheckPage"
              />
            </el-row>
          </el-col>
          <el-col :span="8">
            <el-row type="flex" justify="end">
              <el-button
                class="me-4 mb-2 mt-3"
                type="primary"
                @click="loadDraftChecks"
              >
                <BIconArrowRepeat class="bicon" style="margin-right: 1px;" /> Refresh
              </el-button>
            </el-row>
          </el-col>
        </el-row>
        <TransactionsConfPaymentList
          v-if="draftChecks"
          name="draftChecks_transactions_conf_list"
          ref="draftChecks_conf_list"
          :aggridColumns="draftAggridColumns"
          :rows="draftChecks"
          :emitSelect="true"
          :baseQuery="baseQuery"
          :currentPage="currentCheckPage"
          :autoLoad="false"
          @onSelect="draftCheckSelectionChange"
        />
      </el-col>
    </el-row>
    <CheckPrinter
      :checksToPrint="getCheckrunQuote"
      v-model:selectedBank="selectedBank"
      v-model:postedChecks="postedChecks"
      :getNumOfChecksToPrintPerBank="getNumOfChecksToPrintPerBank"
      v-model:checkPrinterVisible="checkPrinterVisible"
      :draft-checks="selectedDraftChecks"
      @updatePostedChecks="updatePostedChecks"
      @resetParentFields="resetParentFields"
      @printChecks="printChecks"
    />
    <el-dialog
      title="Confirm Signature"
      v-model="confirmSignatureDialog"
      width="30%"
      :show-close="false"
      :close-on-press-escape="true"
      :close-on-click-modal="false">
      <span>
        Are you sure you want to request digital signatures for the selected checks?
      </span>
      <div class="mt-3">
        <span>Number of signatures: </span>
        <select
          v-model="numberOfSignatures">
          <option>1</option>
          <option>2</option>
        </select>
      </div>
      <div v-for="signature in numberOfSignatures" :key="signature" class="mt-2">
        <label for="`signatureSelect${signature}`">Signature {{ signature }}: </label>
        <select @change="selectSignatureUser($event, signature)" name="`signatureSelect${signature}`">
          <option v-for="option in signatureUserChoices" :value="option.userId" :key="option"> {{ option.text }} </option>
        </select>
      </div>
      <template v-slot:footer><span class="dialog-footer">
        <el-button class="exit-modal-btn" type="primary" @click="confirmSignatureDialog = false">
          No
        </el-button>
        <el-button class="exit-modal-btn" type="primary" @click="requestSignatureApprovals">
          Yes
        </el-button>
      </span></template>
    </el-dialog>
  </el-col>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { notify } from '@/utils/helpers/Notify'
  import store from '@/store/store'
  import CheckPrinter from '@/components/Programs/CheckProcessing/CheckPrinter.vue'
  import Filters from '@/components/Filters.vue'
  import SelectFilter from '@/components/FilterComponents/SelectFilter'
  import AutocompleteFilter from '@/components/FilterComponents/AutocompleteFilter.vue'
  import HeaderInfoText from '@/components/HeaderInfoText'
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'
  import { handleSelectionChange } from '@/components/RouteComponents/commonFunctions'
  import { formatLocale } from '@/utils/helpers/StringsNumbers'
  import { DataLoader } from '@/utils/helpers/DataLoader/DataLoader'
  import { payload as bankActionPayload } from '@/components/RouteComponents/Banks/actionPayload'
  import BaseResource from '@sigmacloud/sigma-client/dist/resources/base'
  import BatchResource from '@sigmacloud/sigma-client/dist/resources/transactions/batch'
  import UserResource from '@sigmacloud/sigma-client/dist/resources/user'
  import BankResource from '@sigmacloud/sigma-client/dist/resources/system/bank'
  import CheckResource from '@sigmacloud/sigma-client/dist/resources/transactions/check'
  import TransactionsConfPaymentList from '@/components/SigmaClientNew/TransactionsConfPaymentList.vue'

  export default {
    components: {
      CheckPrinter,
      Filters,
      SelectFilter,
      AutocompleteFilter,
      HeaderInfoText,
      TransactionsConfPaymentList
    },
    data () {
      return {
        tableHeight: 1000,
        batchUpdateSelection: null,
        selection: [],
        transactionsShown: [],
        loadingOptions: false,
        options: [],
        searchTimeout: null,
        checkPrinterVisible: false,
        selectedBank: null,
        postedChecks: [],
        fetchingCheckData: false,
        changingPage: false,
        receiving: false,
        loadingBatchOptions: false,
        confirmSignatureDialog: false,
        numberOfSignatures: 1,
        signatureUsers: [],
        signatureUserChoices: [],
        signatureUser1: undefined,
        signatureUser2: undefined,
        currentUser: undefined,
        selectedDraftChecks: [],
        draftChecktableKey: 0,
        draftChecksSelected: false,
        printableDraftCheckResource: undefined,
        numPrintableDraftChecks: 0,
        currentDraftCheckPage: 1,
        draftChecksLoading: false,
        // Whether the checks waiting on a signature have been asked for the rows they open on
        draftChecksGridLoaded: false,
        aggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'vendorColumn', 'descriptionColumn', 'amountColumn', 'dueDateColumn', 'batchColumn', 'bankColumn'],
        draftAggridColumns: ['rowSelect', 'ledgerColumn', 'numberColumn', 'vendorColumn', 'descriptionColumn', 'amountColumn', 'batchColumn', 'signatureApprovalsColumn', 'bankColumn']
      }
    },
    methods: {
      getAppliedFilters () {
        const filterModel = this.gridFilterModel()
        sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ model: filterModel, page: this.currentPage }))
        this.$store.dispatch('setFilter', { page: this.currentPage, ...this.currentFilters })
        this.$refs['check_conf_list'].applyFiltersonGrid(filterModel)
      },
      gridFilterModel () {
        const asList = (value) => Array.isArray(value) ? value : (value === '' || value === null || value === undefined ? [] : [value])
        const named = (getter, ids) => ids
          .map(id => ({ id, name: (getter(id) || {}).name }))
          .filter(record => record.name)
        const searchModel = (colId, records) => ({
          filterType: colId,
          values: records.map(record => record.id),
          names: records.map(record => record.name)
        })
        const model = {}

        const bankNames = named(this.getBankById, asList(this.currentFilters.batch__bank)).map(bank => bank.name)
        if (bankNames.length) model.bank = { filterType: 'set', values: bankNames }

        const batches = named(this.batch, asList(this.currentFilters.batch))
        if (batches.length) model.batch = searchModel('batch', batches)

        const vendors = named(this.getVendorById, asList(this.currentFilters.vendor))
        if (vendors.length) model.vendor = searchModel('vendor', vendors)

        return model
      },
      /**
       * Handles transaction selection
       * @param selection
       */
      handleSelectionChange (selection) {
        handleSelectionChange(this, selection)
      },
      /**
       * Asks the checks waiting on a signature for their rows, now that the printable APs have
       * theirs. The list above reloads whenever it is filtered or paged through, and this is only
       * the one load the grid was held back from, so it is asked for once.
       */
      loadDraftChecksGrid () {
        if (this.draftChecksGridLoaded) return
        this.draftChecksGridLoaded = true
        const grid = this.$refs['draftChecks_conf_list']
        if (grid) grid.loadInitialRows()
      },
      draftCheckSelectionChange (selection) {
        this.selectedDraftChecks = selection
        if (this.selectedDraftChecks.length) {
          this.draftChecksSelected = true
        } else {
          this.draftChecksSelected = false
        }
      },
      async filtersApplied () {
        if (this.currentFilters['batch__bank']) {
          this.$refs.batch__bank_filter.selectedVal = this.currentFilters['batch__bank'][0]
        }

        const vendorId = this.currentFilters['vendor'] ? this.currentFilters['vendor'][0] : ''
        this.syncAutocompleteFilter(this.$refs.vendor_filter, 'vendor', vendorId, vendorId ? this.getVendorById(vendorId) : null)
        if (!vendorId) {
          await this.$store.dispatch('unsetFilter', 'vendor')
          this.$store.dispatch('clearVendorState')
        }

        const batchId = this.currentFilters['batch'] ? this.currentFilters['batch'][0] : ''
        this.syncAutocompleteFilter(this.$refs.batch_filter, 'batch', batchId, batchId ? this.batch(batchId) : null)
        if (!batchId) {
          await this.$store.dispatch('unsetFilter', 'batch')
          this.$store.dispatch('clearBatchState')
        }

        this.$store.dispatch('setFilter', { page: this.currentPage, ...this.currentFilters })
      },
      syncAutocompleteFilter (filter, filterName, id, record) {
        if (!filter) return
        const name = (record && record.name) || ''
        filter.keyword = name
        filter.selection = id ? { label: name, value: id } : {}
        if (filter.properties && typeof filter.properties.sync === 'function') {
          filter.properties.sync(filterName, id)
        }
      },
      /**
       * Handles paginating between PC pages
       * @param page  Target page
       */
      async changePage (page) {
        this.changingPage = true
        this.receiving = true
        try {
          if (!(this.currentFilters.batch__bank || this.currentFilters.batch)) {
            await this.setDefaultBank()
          }
          await this.$store.commit('UPDATE_CURRENT_CHECK_PAGE', page || 1)
          await this.$store.dispatch('clearApState', [])
          this.currentPage = page
          await this.$store.dispatch('fetchPrintableApsWithQuery', { ...this.currentFilters, page })
          const filterModel = this.gridFilterModel()
          sessionStorage.setItem(`gridFilterModel:${this.$route.name}`, JSON.stringify({ model: filterModel, page: page }))
          this.$store.dispatch('setFilter', { page: page, ...this.currentFilters })
          setTimeout(() => {
            this.changingPage = false
            this.receiving = false
          })
          this.restoreSelection()
        } catch (e) {
          console.log(e)
          this.changingPage = false
        }
      },
      async changeDraftCheckPage (page) {
        this.draftChecksLoading = true
        try {
          await this.$store.dispatch('fetchPrintableChecksWithQuery', { ...this.currentFilters, page: page })
          setTimeout(() => {
            this.draftChecktableKey++
            this.draftChecksLoading = false
          })
          this.$refs['draftChecks_conf_list'].restoreSelection(this.selection)
        } catch (e) {
          console.log(e)
          this.draftChecksLoading = false
        }
      },
      /**
       * Preserves the selected transactions and shows them as selected whenever the table is re-rendered
       * due to a page or tab change
       */
      restoreSelection () {
        this.$refs['check_conf_list'].restoreSelection(this.selection)
      },
      async loadDraftChecks () {
        this.draftChecksLoading = true
        this.changingPage = true
        this.receiving = true
        try {
          await this.$store.dispatch('clearCheckState', [])
          await this.$store.dispatch('resetCheckMeta')
          await this.$store.dispatch('fetchPrintableChecksWithQuery', { ...this.currentFilters })
          setTimeout(() => {
            this.changingPage = false
            this.receiving = false
            this.draftChecktableKey++
            this.draftChecksLoading = false
          })
        } catch (e) {
          console.log(e)
          this.draftChecktableKey++
          this.changingPage = false
          this.receiving = false
          this.draftChecksLoading = false
        }
      },
      /**
       * Sets selected banks, grabs a check run quote, fetches starting check numbers, and
       * starts the check printer program
       */
      printChecks () {
        this.selectedBank = this.currentFilters.batch__bank
        if (this.selectedDraftChecks.length && !this.selection.length) {
          this.fetchingCheckData = false
          this.checkPrinterVisible = true
          return
        }
        let ids = this.selection.map(v => v.id)
        this.fetchingCheckData = true
        this.$store.dispatch('fetchCheckrunQuote', ids)
          .then(r => {
            if (!r) return notify(this, 'error', 'Error fetching check run quote')
            return this.fetchStartingCheckNumbers()
          })
          .then(() => {
            this.fetchingCheckData = false
            this.checkPrinterVisible = true
          })
          .catch(e => {
            notify(this, 'error', e && e.statusText ? e.statusText : 'Error starting check printer program')
            this.fetchingCheckData = false
          })
      },
      async getSignatureUsers () {
        let signatureResource = BaseResource.wrap('/signatures')
        let signatureResponse = await signatureResource.get({ params: { page_size: 100 } })
        let { data } = signatureResponse
        let { results } = data
        let signatureUserIds = []
        let signatureUserResources = []
        let userChoices = [{ userId: undefined, signatureId: undefined, text: '-' }]
        for (let result of results) {
          signatureUserIds.push(result.user)
        }
        for (let userId of signatureUserIds) {
          let userResource = await UserResource.detail(userId)
          signatureUserResources.push(userResource)
        }
        this.signatureUsers = signatureUserResources
        for (let userResource of signatureUserResources) {
          let signature = results.find((result) => {
            return result.user === userResource.id
          })
          let choice = {
            userId: userResource.id,
            signatureId: signature.id,
            text: `${userResource.attributes.first_name} ${userResource.attributes.last_name} - ${userResource.attributes.username} - ${userResource.attributes.email}`
          }
          if (userResource.id === this.currentUser) {
            choice.text += ' (myself)'
          }
          userChoices.push(choice)
        }
        this.signatureUserChoices = userChoices
      },
      selectSignatureUser (event, signature) {
        this[`signatureUser${signature}`] = parseInt(event.target.value)
      },
      getCurrentUser () {
        let userId = this.$store.getters.getUser.userId
        this.currentUser = userId
      },
      async requestSignatureApprovals () {
        let signatureChoices = {
          choice1: undefined,
          choice2: undefined
        }
        if (this.signatureUser1) {
          let choice = this.signatureUserChoices.find((choice) => {
            return choice.userId === this.signatureUser1
          })
          signatureChoices.choice1 = choice
        }
        if (this.signatureUser2) {
          let choice = this.signatureUserChoices.find((choice) => {
            return choice.userId === this.signatureUser2
          })
          signatureChoices.choice2 = choice
        }
        let choiceNames = Object.keys(signatureChoices)
        for (let choice of choiceNames) {
          if (signatureChoices[choice] !== undefined) {
            // choice contains userId and signatureId
            // need to generate checks from selection

            // Step 1: Fetch quotes - need AP ids
            let selectedAps = []
            for (let ap of this.selection) {
              selectedAps.push(ap.id)
            }
            let queryUrlString = '/runs/quote?ap__id__in='
            for (let ap of selectedAps) {
              queryUrlString += `${ap},`
            }
            let cleanedUrlString = queryUrlString.substring(0, queryUrlString.length - 1)
            let checkRunQuoteResource = BaseResource.wrap(cleanedUrlString)
            let checkRunQuoteResponse = await checkRunQuoteResource.get()
            let checkRunQuotes = []
            for (let quote of checkRunQuoteResponse.data) {
              checkRunQuotes.push(quote)
            }

            // Step 2: Save batches
            let quotesWithBatches = await this.saveBatches(checkRunQuotes)
            let reFlattenedQuotes = []
            let apIds = Object.keys(quotesWithBatches)
            for (let apId of apIds) {
              reFlattenedQuotes.push(...quotesWithBatches[apId])
            }

            // Step 3: Organize quotes by bank
            let quoteByBanks = {}
            for (let quote of reFlattenedQuotes) {
              quoteByBanks[`${quote.batch.bank}`] = [quote]
            }

            // Step 4: Assign numbers to checks
            let numberedChecks = await this.getCheckNumbers(quoteByBanks)

            // Step 5: Save checks
            let savedChecks = await this.saveChecks(numberedChecks)

            // Step 6:  POST new signature requests with check numbers
            let signatureRequestPayload = {
              paper_check: undefined,
              signature: signatureChoices[choice].signatureId,
              requested_by: this.currentUser,
              requested_user: signatureChoices[choice].userId,
              request_type: 'SIGNATURE'
            }

            for (let check of savedChecks) {
              signatureRequestPayload.paper_check = check.id
              let signatureRequestResource = BaseResource.wrap('/checkapprovals')
              await signatureRequestResource.post(signatureRequestPayload)
            }
          }
        }
        notify(this, 'success', 'Your check signature request has been sent to the specified signer(s)')
        this.confirmSignatureDialog = false
      },
      async saveBatches (quotedChecks) {
        // Group quotes by AP
        let groupedQuotes = {}
        for (let quote of quotedChecks) {
          let apId = quote.related_transactions[0].id
          if (groupedQuotes[apId]) {
            groupedQuotes[apId].push(quote)
          } else {
            groupedQuotes[apId] = [quote]
          }
        }
        // Quotes are grouped by AP
        // Need to create one batch per AP, then assign the resulting id to the existing check quotes
        let apIds = Object.keys(groupedQuotes)
        for (let apId of apIds) {
          let batchResource = new BatchResource()
          let batchData = groupedQuotes[apId][0].batch
          let batchKeys = Object.keys(batchData)
          for (let key of batchKeys) {
            batchResource.set(key, batchData[key])
          }
          await batchResource.save()

          for (let ap of groupedQuotes[apId]) {
            ap.batch.id = batchResource.id
          }
        }

        return groupedQuotes
      },
      async getCheckNumbers (groupedPayrolls) {
        let bankIds = Object.keys(groupedPayrolls)
        let quotedChecks = []
        for (let bankId of bankIds) {
          let numChecks = groupedPayrolls[bankId].length

          let bankRes = BankResource.wrap(`/${bankId}/checks?amount=${numChecks}`)
          let { data } = await bankRes.get()
          for (let index = 0; index < numChecks; index++) {
            groupedPayrolls[bankId][index].number = String(data[index])
          }
          quotedChecks.push(...groupedPayrolls[bankId])
        }
        return quotedChecks
      },
      async saveChecks (checkData) {
        let checks = []
        let savedChecks = []
        for (let check of checkData) {
          let newCheckResource = new CheckResource()
          for (let attribute of Object.keys(check)) {
            newCheckResource.set(attribute, check[attribute])
          }
          newCheckResource.set('status', 'DRAFT')
          checks.push(newCheckResource)
        }
        for (let check of checks) {
          await check.save()
          savedChecks.push(check)
        }
        return savedChecks
      },

      /**
       * Sets selected banks, grabs a check run quote, fetches starting check numbers, and
       * starts the check printer program
       */
      signChecks () {
        this.confirmSignatureDialog = false
        this.selectedBank = this.currentFilters.batch__bank

        // if (checks are updated successfully)
        notify(this, 'success', 'Checks will contain your signature.  Please click on "Print Selected Checks" when ready.')
        // else do something else
      },
      /**
       * Fetches starting check numbers
       * @return {Promise.<*[]>}
       */
      async fetchStartingCheckNumbers () {
        try {
          const amount = this.getNumOfChecksToPrintPerBank()[this.selectedBank]
          const resp = await this.$store.dispatch('fetchCheckNumbers', { id: this.selectedBank, params: { amount } })
          if (!resp) {
            throw new Error('Unable to fetch check numbers')
          }
        } catch (e) {
          console.log(e)
        }
      },
      /**
       * Calculates the number of checks to print per bank, based on how many times a specific bank
       * is referenced across all selected APs
       * @return {{}}   An object containing the bank id as the property and frequency as its value
       */
      getNumOfChecksToPrintPerBank () {
        let ret = {}
        this.getCheckrunQuote.forEach(check => {
          const bankId = check.batch.bank
          if (!ret[bankId]) {
            ret[bankId] = 1
          } else {
            ret[bankId]++
          }
        })
        return ret
      },
      /**
       * Updates the list of available checks after one or more checks have been posted
       */
      updatePostedChecks (newData) {
        this.postedChecks = newData
        this.refreshData()
      },
      /**
       * Resets certain fields after checks print or after user cancels check printing
       */
      resetParentFields () {
        this.selection = []
        this.$store.dispatch('clearCheckNumbers')
        this.checkPrinterVisible = false
        this.selectedBank = null
      },
      /**
       * Refreshes the list of APs after one or more checks have been posted. First, clears stale data, then gets the
       * batches which belong to the currently selected bank. Then, fetches printable APs, preserving any existing
       * filter criteria.
       */
      async refreshData () {
        this.receiving = true
        try {
          const dataLoader = new DataLoader(this.$store, [ bankActionPayload ])
          let initPromises = [
            this.$store.dispatch('clearApState', true),
            this.$store.dispatch('clearBatchState', true),
            this.$store.dispatch('clearVendorState'),
            dataLoader.loadData()
          ]
          await Promise.all(initPromises)
          await this.$store.dispatch('fetchPrintableApsWithQuery', this.currentFilters)
          await this.$store.dispatch('fetchCurrentAndNextPeriod')
          this.transactionsShown = this.transactions
        } catch (e) {
          console.log(e)
          notify(this, 'warning', 'Failed to refresh data')
        } finally {
          this.receiving = false
        }
      },
      /**
       * Sets the default bank in bank filter
       */
      async setDefaultBank () {
        if (this.getDefaultBank && Object.keys(this.getDefaultBank).length) {
          await this.$store.dispatch('setDefaultSelection', { bank: {
            label: this.getDefaultBank.name,
            value: this.getDefaultBank.id
          }})
        }
      },
      capitalizeFirstLetter,
      /**
       * Formats dollar amounts with thousands separators
       * @param value
       * @return {*}
       */
      formatAmount (value) {
        return value ? formatLocale(value) : ''
      },
      formatApprovalsColumn (value) {
        return value.signature_status
      },
      /**
       * Event handler that fires whenever the filters change. If the 'batch__bank' filter changes, clears the batch
       * options from the batch select filter, then fetches batches associated with the new 'batch__bank' value.
       * @param filterName  The name of the filter that changed
       * @param selectedVal The new value
       */
      async handleFilterChange ({ filterName = '', selectedVal = '' }) {
        try {
          if (filterName === 'batch__bank' && selectedVal !== this.currentFilters.batch__bank) {
            const batchFilter = this.getFilterRef('batch_filter', 0)
            if (batchFilter && batchFilter.hasOwnProperty('selectedVal')) {
              batchFilter.selectedVal = []
              batchFilter.change()
              // await this.loadDraftChecks(selectedVal)
            }
          }
        } catch (e) {
          console.log('Failed to fetch new data after applying filters: ' + e)
        }
      }
    },
    computed: {
      ...mapGetters({
        transactions: 'getAps',
        draftChecks: 'getChecks',
        getVendorById: 'getVendorById',
        batch: 'getBatchById',
        banks: 'getBanks',
        batches: 'getBatches',
        currentPage: 'getCurrentApPage',
        currentCheckPage: 'getCurrentCheckPage',
        getDefaultBatch: 'getDefaultBatch',
        getDefaultBank: 'getDefaultBank',
        getBankById: 'getBankById',
        getBankByBatchId: 'getBankByBatchId',
        getCompanyByBatchId: 'getCompanyByBatchId',
        getCompanyById: 'getCompanyById',
        getCheckrunQuote: 'getCheckrunQuote',
        currentPeriod: 'getCurrentPeriod',
        meta: 'getApMetadata',
        draftChecksMeta: 'getCheckMetadata',
        navigating: 'getNavigationStatus',
        currentFilters: 'getFilters'
      }),
      /**
       * Available filters
       */
      filters () {
        return [
          {
            type: 'selectFilter',
            props: {
              filterName: 'batch__bank',
              filterable: true,
              clearable: false,
              opts: this.$store.getters.getAllBanks.map(bank => ({ label: bank.name, value: bank.id })),
              defaults: [this.getDefaultBank.id],
              multi: false,
              placeholder: 'Select a bank',
              applyOnSelection: true
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'batch',
              remote: true,
              clearable: true,
              action: 'fetchBatchesWithQuery',
              clearAction: 'clearBatchState',
              getter: 'getBatchesBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Batch',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          },
          {
            type: 'autocompleteFilter',
            props: {
              filterName: 'vendor',
              remote: true,
              clearable: true,
              action: 'fetchVendorsWithQuery',
              clearAction: 'clearVendorState',
              getter: 'getVendorsBySearchCriteria',
              triggerOnFocus: false,
              placeholder: 'Vendor',
              queryParamType: 'search',
              suggestionLabelAttr: 'name'
            }
          }
        ]
      },
      /**
       * Returns a boolean indicating whether the filters' "Apply" button should be disabled. This is useful
       * because this particular view requires that all printable APs being displayed be from one bank, so the
       * user cannot be allowed to clear the filters and hit "Apply", because that could return printable APs
       * from multiple batches/banks.
       */
      filteringShouldBeDisabled () {
        return !this.currentFilters.batch && !this.currentFilters.batch__bank && !this.currentFilters.vendor
      },
      /**
       * Returns a boolean indicating whether a selection was made. Used to enable/disable the "Print Selected Checks"
       * button.
       */
      printableApsSelected () {
        return (!!this.selection.length || !!this.selectedDraftChecks.length)
      },
      /**
       * Computed property that returns a boolean indicating whether the loading overlay should be shown
       */
      showLoader () {
        return (this.receiving || this.changingPage) && !this.navigating && !this.checkPrinterVisible
      },
      baseQuery () {
        return { batch__bank: [this.getDefaultBank.id] }
      }
    },
    watch: {
      transactions () {
        this.transactionsShown = this.transactions
      },
      numberOfSignatures (newValue) {
        if (typeof newValue === 'number') return
        this.numberOfSignatures = parseInt(newValue)
      }
    },
    /**
     * Pre-route enter logic. Clears AP, Batch, and Vendor state, fetches the active period,
     * fetches printable APs, then finds and fetches dependencies for printable APs.
     * Since the options in the batch filter are now dependent on the selected bank, and since the component must
     * initialize with results already filtered by default bank, then the active batches associated with the
     * default bank must also be fetched so that batch filter options are available once the component renders. This
     * is done after the initial set of printable APs has been fetched.
     * @param to
     * @param from
     * @param next
     */
    async beforeRouteEnter (to, from, next) {
      try {
        let dataLoader = new DataLoader(store, [ bankActionPayload ])
        const promises = [
          store.dispatch('clearApState', true),
          store.dispatch('clearBatchState'),
          store.dispatch('clearVendorState'),
          store.dispatch('fetchPeriodsWithQuery', { active: 'True' }),
          store.dispatch('fetchUiSetting', { name: 'CHECK_DATE_RANGE' }),
          store.dispatch('fetchUiSetting', { name: 'BT_NUMBER_SETTINGS' }),
          dataLoader.loadData()
        ]
        await Promise.all(promises)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    },
    async created () {
      this.setDefaultBank()
    },
    async beforeMount () {
      // await this.loadDraftChecks()
    },
    async mounted () {
      this.$nextTick = () => {
        const tableHeight = () => {
          const parentHeight = document.getElementById('check_processing').offsetHeight
          const headerHeight = document.getElementById('check_processing-header').offsetHeight
          return parentHeight - headerHeight
        }
        this.tableHeight = tableHeight()
      }

      this.getCurrentUser()
      await this.getSignatureUsers()
    },
    /**
     * Pre-route leave housecleaning
     * @param to
     * @param from
     * @param next
     * @return {Promise<void>}
     */
    async beforeRouteLeave (to, from, next) {
      try {
        await Promise.all([
          this.$store.dispatch('clearFilters'),
          this.$store.dispatch('clearBatchState', true),
          this.$store.dispatch('clearVendorState', true),
          this.$store.dispatch('clearApState', true)
        ])
        sessionStorage.removeItem(`gridFilterModel:${this.$route.name}`)
      } catch (e) {
        console.log(e)
      } finally {
        next()
      }
    }
  }
</script>

<style scoped>
  /*Input form table style properties*/
  table {
    border-spacing: 0;
    border: 1px solid #bfcbd9;
    background-color: #efefef;
  }
  /*Table style properties*/
  .h-padded {
    padding: 0 12px;
  }
  .v-padded-s {
    padding: 12px 0;
  }
  .left-aligned-col {
    text-align: left;
  }
  #check_processing-header {
    border-bottom: 1px solid lightgray;
  }
  .el-table .modified-row {
    background: #c9e5f5;
  }
</style>